import React, { useEffect, useMemo, useState } from "react";
import { Search, Download, MessageCircle, ChevronDown, ChevronRight, Plus, Loader2, Layers, List, CheckCircle2, AlertTriangle, TrendingUp, ExternalLink, Trash2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const shortId = (s) => (s ? s.slice(0, 8) : "—");

const RANGES = [
  { key: "7", label: "Last 7 days" },
  { key: "30", label: "Last 30 days" },
  { key: "month", label: "This month" },
  { key: "all", label: "All time" },
];

function csvEscape(v) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function normalize(q) {
  return (q || "").toLowerCase().replace(/[^\w\s]/g, "").replace(/\s+/g, " ").trim();
}
function inRange(created, range) {
  if (range === "all") return true;
  const t = new Date(created);
  const now = new Date();
  if (range === "month") return t.getFullYear() === now.getFullYear() && t.getMonth() === now.getMonth();
  const days = Number(range);
  return t >= new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
}

export default function CassieQuestionsManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("all"); // all | conversation
  const [range, setRange] = useState("all");
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [open, setOpen] = useState({});
  const [promoted, setPromoted] = useState({});
  const [promoting, setPromoting] = useState(null);

  const [clicks, setClicks] = useState([]);

  useEffect(() => {
    adminApi.chatMessages.list().then(setItems).catch(() => {}).finally(() => setLoading(false));
    adminApi.chatMessages.linkClicks().then((r) => setClicks(r.clicks || [])).catch(() => {});
  }, []);

  // Deleting a transcript on request is part of the retention policy, not a
  // developer job — so it lives here next to the transcript.
  const removeMessage = async (m) => {
    if (!window.confirm("Delete this message and Cassie's reply? This cannot be undone.")) return;
    await adminApi.chatMessages.remove(m.id);
    setItems((list) => list.filter((x) => x.id !== m.id));
    toast({ title: "Message deleted" });
  };

  const removeConversation = async (session) => {
    if (!window.confirm("Delete this whole conversation? This cannot be undone.")) return;
    await adminApi.chatMessages.removeSession(session);
    setItems((list) => list.filter((x) => x.session_id !== session));
    toast({ title: "Conversation deleted" });
  };

  // Date-filtered set drives the insights (independent of search / unanswered toggle)
  const dateFiltered = useMemo(() => items.filter((m) => inRange(m.created_at, range)), [items, range]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return dateFiltered.filter((m) => {
      if (unansweredOnly && !m.lets_talk) return false;
      if (term && !((m.question || "").toLowerCase().includes(term) || (m.answer || "").toLowerCase().includes(term))) return false;
      return true;
    });
  }, [dateFiltered, q, unansweredOnly]);

  const insights = useMemo(() => {
    const unanswered = dateFiltered.filter((m) => m.lets_talk).length;
    const catCount = {};
    const normCount = {};
    dateFiltered.forEach((m) => {
      const c = m.category || "General";
      catCount[c] = (catCount[c] || 0) + 1;
      const n = normalize(m.question);
      if (n) { if (!normCount[n]) normCount[n] = { count: 0, sample: m.question }; normCount[n].count += 1; }
    });
    const topTopics = Object.entries(catCount).sort((a, b) => b[1] - a[1]).slice(0, 6);
    const topQuestions = Object.values(normCount).filter((x) => x.count >= 2).sort((a, b) => b.count - a.count).slice(0, 6);
    return { total: dateFiltered.length, unanswered, topTopics, topQuestions };
  }, [dateFiltered]);

  // Cassie's replies point people somewhere; this is whether they went.
  const links = useMemo(() => {
    const clicked = clicks.filter((c) => inRange(c.created_at, range));
    const counts = {};
    clicked.forEach((c) => { counts[c.root || c.path] = (counts[c.root || c.path] || 0) + 1; });
    return {
      pricingMentions: dateFiltered.filter((m) => m.pricing_link || (m.answer || "").includes("/pricing")).length,
      pricingClicks: clicked.filter((c) => (c.root || c.path) === "/pricing").length,
      byPath: Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8),
    };
  }, [dateFiltered, clicks, range]);

  const conversations = useMemo(() => {
    const map = new Map();
    filtered.forEach((m) => {
      const k = m.session_id || "unknown";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(m);
    });
    const groups = Array.from(map.entries()).map(([session, msgs]) => {
      const ordered = [...msgs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      return { session, msgs: ordered, last: ordered[ordered.length - 1]?.created_at };
    });
    groups.sort((a, b) => new Date(b.last) - new Date(a.last));
    return groups;
  }, [filtered]);

  const promoteToFaq = async (m) => {
    setPromoting(m.id);
    try {
      await adminApi.chatFaqs.create({ question: m.question || "", answer: m.answer || "", order: 0, category: "" });
      setPromoted((p) => ({ ...p, [m.id]: true }));
      toast({ title: "Added to Cassie's knowledge base", description: "Find and refine it under Cassie → knowledge & FAQs." });
    } catch {
      toast({ title: "Couldn't add to FAQs", variant: "destructive" });
    } finally { setPromoting(null); }
  };

  const exportCsv = () => {
    const header = ["Date", "Session", "Topic", "Question", "Cassie answer", "Unanswered"];
    const rows = [...dateFiltered]
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .map((m) => [fmt(m.created_at), m.session_id || "", m.category || "", m.question || "", m.answer || "", m.lets_talk ? "Yes" : "No"]);
    const csv = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = "cassie-questions.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const QARow = ({ m, showAsk = true }) => {
    const isOpen = open[m.id];
    const un = m.lets_talk;
    return (
      <div className={`rounded-xl border overflow-hidden bg-white ${un ? "border-amber-200" : "border-slate-100"}`} data-testid={`cassie-q-${m.id}`}>
        <button onClick={() => setOpen((o) => ({ ...o, [m.id]: !o[m.id] }))} className="w-full flex items-start gap-3 p-4 text-left hover:bg-slate-50 transition-colors">
          {isOpen ? <ChevronDown size={16} className="text-slate-400 mt-0.5 shrink-0" /> : <ChevronRight size={16} className="text-slate-400 mt-0.5 shrink-0" />}
          <span className="flex-1 min-w-0">
            <span className="font-semibold text-navy text-sm block">{m.question || "—"}</span>
            <span className="text-xs text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
              {fmt(m.created_at)}
              {m.category && <span className="text-slate-400">· {m.category}</span>}
              {showAsk && <span className="text-slate-300">· session {shortId(m.session_id)}</span>}
              {un && <span className="inline-flex items-center gap-1 text-amber-600 font-semibold"><AlertTriangle size={11} /> Cassie couldn't answer</span>}
            </span>
          </span>
        </button>
        {isOpen && (
          <div className="px-4 pb-4 pl-10 space-y-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">Cassie replied</p>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{m.answer || "—"}</p>
            </div>
            <button
              onClick={() => removeMessage(m)}
              data-testid={`cassie-delete-${m.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-red-500 border border-red-100 hover:bg-red-50 px-3 py-1.5 rounded-full ml-2"
            >
              <Trash2 size={13} /> Delete this message
            </button>
            {promoted[m.id] ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal"><CheckCircle2 size={14} /> Added to Cassie's FAQs</span>
            ) : (
              <button onClick={() => promoteToFaq(m)} disabled={promoting === m.id} data-testid={`cassie-promote-${m.id}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-navy border border-navy/15 hover:bg-slate-50 px-3 py-1.5 rounded-full disabled:opacity-50">
                {promoting === m.id ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Turn into an FAQ
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="cassie-questions-manager">
      <h1 className="font-display font-extrabold text-2xl text-navy mb-1">Cassie Questions</h1>
      <p className="text-sm text-slate-500 mb-5">Every question visitors have asked Cassie. {items.length} message(s) captured. Search, spot the top topics, review answers, and turn common questions into curated FAQs.</p>

      {/* Date range */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {RANGES.map((r) => (
          <button key={r.key} data-testid={`cassie-range-${r.key}`} onClick={() => setRange(r.key)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${range === r.key ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-navy/30"}`}>{r.label}</button>
        ))}
      </div>

      {/* Insights */}
      {!loading && items.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="cassie-insights">
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5"><TrendingUp size={14} /> Top topics</p>
            {insights.topTopics.length ? (
              <div className="flex flex-wrap gap-2">
                {insights.topTopics.map(([cat, n]) => (
                  <button key={cat} onClick={() => setQ("")} className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 text-xs font-semibold text-navy">
                    {cat} <span className="bg-navy text-white rounded-full px-1.5 py-0.5 text-[10px]">{n}</span>
                  </button>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400">No questions in this period.</p>}
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 ${insights.unanswered ? "bg-amber-50 text-amber-600" : "bg-teal/10 text-teal"}`}>
                <AlertTriangle size={13} /> {insights.unanswered} unanswered
              </span>
              <span className="text-xs text-slate-400">of {insights.total} in this period</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="cassie-most-asked">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5"><MessageCircle size={14} /> Most asked</p>
            {insights.topQuestions.length ? (
              <ul className="space-y-2">
                {insights.topQuestions.map((x, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="bg-orange/10 text-orange font-bold rounded-full px-2 py-0.5 text-[11px] shrink-0">×{x.count}</span>
                    <button onClick={() => setQ(x.sample)} className="text-navy text-left hover:text-orange leading-snug">{x.sample}</button>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-slate-400">No question has been asked more than once in this period yet.</p>}
          </div>

          {/* Where the chat sends people, and whether they go */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 md:col-span-2" data-testid="cassie-link-stats">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5">
              <ExternalLink size={14} /> Where Cassie sends people
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <p className="font-display font-extrabold text-2xl text-navy">{links.pricingMentions}</p>
                <p className="text-xs text-slate-500 leading-relaxed mt-0.5">
                  replies pointing at the pricing page
                  {insights.total ? ` — ${Math.round((links.pricingMentions / insights.total) * 100)}% of replies` : ""}
                </p>
              </div>
              <div>
                <p className="font-display font-extrabold text-2xl text-navy">{links.pricingClicks}</p>
                <p className="text-xs text-slate-500 leading-relaxed mt-0.5">followed through to the pricing page</p>
              </div>
              <div>
                <p className="font-display font-extrabold text-2xl text-navy">
                  {links.pricingMentions ? `${Math.round((links.pricingClicks / links.pricingMentions) * 100)}%` : "—"}
                </p>
                <p className="text-xs text-slate-500 leading-relaxed mt-0.5">
                  click-through. A reply that mentions the price and a visitor who acts on it aren't the same thing.
                </p>
              </div>
            </div>
            {links.byPath.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                {links.byPath.map(([path, n]) => (
                  <span key={path} className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 text-xs font-semibold text-navy">
                    {path} <span className="bg-navy text-white rounded-full px-1.5 py-0.5 text-[10px]">{n}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="cassie-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions & answers…" className="w-full rounded-full border border-slate-200 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40" />
        </div>
        <button data-testid="cassie-unanswered-toggle" onClick={() => setUnansweredOnly((v) => !v)} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2.5 rounded-full border ${unansweredOnly ? "bg-amber-500 text-white border-amber-500" : "bg-white text-navy border-slate-200 hover:border-amber-300"}`}><AlertTriangle size={14} /> Unanswered only</button>
        <div className="flex bg-slate-100 rounded-full p-1">
          <button data-testid="cassie-view-all" onClick={() => setView("all")} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${view === "all" ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}><List size={14} /> All questions</button>
          <button data-testid="cassie-view-conversation" onClick={() => setView("conversation")} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${view === "conversation" ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}><Layers size={14} /> By conversation</button>
        </div>
        <button data-testid="cassie-export" onClick={exportCsv} disabled={!dateFiltered.length} className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-200 px-4 py-2.5 rounded-full hover:bg-slate-50 disabled:opacity-40"><Download size={15} /> Export CSV</button>
      </div>

      {loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400"><MessageCircle size={36} className="mx-auto mb-3 text-slate-300" />No questions have been asked of Cassie yet.</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-10 text-center text-slate-400">No questions match your filters.</div>
      ) : view === "all" ? (
        <div className="space-y-3">
          {filtered.map((m) => <QARow key={m.id} m={m} />)}
        </div>
      ) : (
        <div className="space-y-6">
          {conversations.map((c) => (
            <div key={c.session} data-testid={`cassie-convo-${c.session}`}>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-2">
                <Layers size={13} /> Conversation {shortId(c.session)} · {c.msgs.length} message(s) · {fmt(c.last)}
                <button
                  onClick={() => removeConversation(c.session)}
                  data-testid={`cassie-delete-convo-${c.session}`}
                  className="inline-flex items-center gap-1 text-red-500 hover:text-red-600 normal-case font-semibold"
                >
                  <Trash2 size={12} /> Delete conversation
                </button>
              </p>
              <div className="space-y-2 border-l-2 border-slate-100 pl-3">
                {c.msgs.map((m) => <QARow key={m.id} m={m} showAsk={false} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
